import { useEffect, useMemo } from 'react';
import { NearMeOutlined } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';

import { useGeolocationContext } from '~providers';
import { FilterLocationOption } from '~types';
import {
  mapGooglePeriodsToOperatingHours,
  parseGooglePlaceInfoForCreateAddress,
} from '~utils/google.util';
import { toastInfo } from '~utils/toast.util';

type Props = {
  onChangeCurrentLocation: (value: FilterLocationOption | null) => void;
  placesServiceRef: React.MutableRefObject<google.maps.places.PlacesService | null>;
};
export const CurrentNearByAddressField: React.FC<Props> = ({
  onChangeCurrentLocation,
  placesServiceRef,
}) => {
  const {
    isGettingCurrentLocation,
    currentLocation,
    handleGetCurrentLocation,
  } = useGeolocationContext();

  const request = useMemo(() => {
    return {
      location: {
        lat: currentLocation?.lat ?? 0,
        lng: currentLocation?.lng ?? 0,
      },
      radius: 10,
    };
  }, [currentLocation?.lat, currentLocation?.lng]);

  // Update venue's address base on current location
  useEffect(() => {
    if (currentLocation) {
      placesServiceRef.current?.nearbySearch(request, (results, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          results?.length
        ) {
          // Get detail of the first address
          placesServiceRef.current?.getDetails(
            { placeId: results[0].place_id || '' },
            (value) => {
              const placeInfo = parseGooglePlaceInfoForCreateAddress(
                value?.address_components,
              );
              const operatingHours = mapGooglePeriodsToOperatingHours(
                value?.opening_hours?.periods,
              );
              onChangeCurrentLocation({
                name: value?.name || '',
                id: value?.place_id || '',
                location: {
                  lat: value?.geometry?.location?.lat() ?? 0,
                  lng: value?.geometry?.location?.lng() ?? 0,
                  infoAddress: {
                    state: placeInfo.state,
                    country: placeInfo.country,
                    countryCode: placeInfo.countryCode,
                    zip: placeInfo.zip,
                    city: placeInfo.city,
                  },
                  operatingHours,
                },
              });
            },
          );
        } else {
          toastInfo('No nearby address found.');
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation, placesServiceRef]);

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      onClick={handleGetCurrentLocation}
      sx={{
        cursor: 'pointer',
      }}
    >
      {isGettingCurrentLocation ? (
        <CircularProgress size={20} />
      ) : (
        <>
          <NearMeOutlined fontSize="small" sx={{ mr: 1 }} />
          <Typography color="text.dark" variant="body2">
            Use current location
          </Typography>
        </>
      )}
    </Box>
  );
};
